import { useState } from "react";
import api from "../lib/api";
import { Sparkles, Loader2, Brain } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { id: "hook", label: "Hook" },
  { id: "script", label: "Script" },
  { id: "concept", label: "Concept" },
  { id: "bio", label: "Bio" },
  { id: "cta", label: "CTA" },
];

const EMOTION_COLOR = {
  curiosity: "#00D4FF", fear: "#EF4444", joy: "#CCFF00",
  anger: "#F59E0B", surprise: "#A78BFA", sadness: "#52525B", trust: "#10B981"
};

export default function ContentAnalyzer() {
  const [form, setForm] = useState({ content: "", content_type: "hook", platform: "tiktok", niche: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    if (!form.content) return toast.error("Colle un contenu à analyser");
    setLoading(true); setResult(null);
    try {
      const r = await api.aiAnalyzeContent(form);
      const m = (r.raw || "").match(/\{[\s\S]*\}/);
      if (m) setResult(JSON.parse(m[0]));
      else toast.error("Format IA invalide");
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  const verdictColor = (v) => ({ EXCELLENT: "#10B981", FORT: "#CCFF00", MOYEN: "#F59E0B", FAIBLE: "#EF4444" }[v] || "#A1A1AA");

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="content-analyzer-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// AI CONTENT ANALYZER</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Disséqué par l'IA</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">Colle un hook, un script, un concept, une bio ou un CTA. Claude Sonnet 4.5 analyse 8 dimensions + propose 3 versions optimisées.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 border border-[#00D4FF]/30 bg-[#00D4FF]/[0.02] rounded-sm p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.5} />
            <h2 className="font-display text-xl font-semibold text-white">Configuration</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(t => (
              <button key={t.id} onClick={() => setForm({ ...form, content_type: t.id })} data-testid={`type-${t.id}`}
                className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider transition ${
                  form.content_type === t.id ? "bg-[#00D4FF] text-black" : "border border-[#1E1E24] text-zinc-400 hover:text-white"
                }`}>{t.label}</button>
            ))}
          </div>
          <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
            placeholder={`Colle ton ${form.content_type} ici...`} rows={6} data-testid="analyzer-content"
            className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#00D4FF] rounded-sm px-3 py-2 text-sm text-white outline-none resize-none" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
              className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
              <option value="tiktok">TikTok</option><option value="instagram">Instagram</option>
              <option value="youtube">YouTube Shorts</option><option value="threads">Threads</option><option value="x">X</option>
            </select>
            <input value={form.niche} onChange={e => setForm({ ...form, niche: e.target.value })} placeholder="Niche"
              className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white outline-none" />
          </div>
          <button onClick={analyze} disabled={loading} data-testid="analyze-btn"
            className="w-full bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-4 py-2.5 rounded-sm font-mono uppercase tracking-wider text-sm flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Analyse..." : "Analyser"}
          </button>
        </div>

        <div className="lg:col-span-3 border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 min-h-[500px]">
          {!result ? (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Lance une analyse pour voir le rapport.</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-[#1E1E24] pb-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Score global</div>
                  <div className="font-mono text-5xl font-bold" style={{ color: verdictColor(result.verdict) }} data-testid="analyzer-score">{result.global_score}/100</div>
                  <div className="font-mono text-sm uppercase tracking-wider mt-1" style={{ color: verdictColor(result.verdict) }}>{result.verdict}</div>
                </div>
                {result.emotion && (
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Émotion dominante</div>
                    <div className="font-display text-2xl font-bold uppercase mt-1" style={{ color: EMOTION_COLOR[result.emotion] || "#fff" }}>{result.emotion}</div>
                  </div>
                )}
              </div>

              {result.dimensions && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">8 Dimensions</div>
                  <div className="grid grid-cols-2 gap-3">
                    {result.dimensions.map((d, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-xs font-mono mb-1">
                          <span className="text-zinc-300 uppercase tracking-wider">{d.name}</span>
                          <span className="text-white">{d.score}</span>
                        </div>
                        <div className="h-1.5 bg-[#1E1E24] rounded-full overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${d.score}%`, background: d.score >= 80 ? "#10B981" : d.score >= 60 ? "#CCFF00" : d.score >= 40 ? "#F59E0B" : "#EF4444" }} />
                        </div>
                        {d.comment && <div className="text-[10px] text-zinc-500 mt-0.5 italic">{d.comment}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.strengths?.length > 0 && (
                <Block label="Points forts" color="#10B981" items={result.strengths} />
              )}
              {result.weaknesses?.length > 0 && (
                <Block label="Points faibles" color="#EF4444" items={result.weaknesses} />
              )}
              {result.improvements?.length > 0 && (
                <Block label="Améliorations" color="#F59E0B" items={result.improvements} />
              )}
              {result.rewritten?.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00] mb-2">3 versions optimisées</div>
                  <div className="space-y-2">
                    {result.rewritten.map((v, i) => (
                      <div key={i} className="border border-[#CCFF00]/30 bg-[#CCFF00]/5 rounded-sm p-3 text-sm text-white italic">"{v}"</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Block({ label, color, items }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color }}>{label}</div>
      <ul className="space-y-1">
        {items.map((i, idx) => <li key={idx} className="text-sm text-zinc-300">· {i}</li>)}
      </ul>
    </div>
  );
}
